import { useCallback, useEffect, useMemo } from "react";
import {
  DEFAULT_NOTE_DIRECTIONS,
  DEFAULT_NOTE_TYPES,
  type Person,
  useDeleteNotesByIdsMutation,
  useGetAllNotesQuery,
  useGetAllPersonsQuery,
} from "@/services/api";
import { isNotVoid } from "@/shared/utils";
import { useAppDispatch, useAppSelector } from "@/services/store";
import {
  notesTableSliceActions,
  notesTableSliceSelectors,
  type NotesTableItem,
} from "@/features/NotesManager/model";
import { notesManagerSelectors, notesManagerActions } from "@/features/NotesManager/model";
import { getErrorMessage } from "@/services/errors";
import { useAlert } from "@/shared/hooks";
import type { Key } from "react";
import type { NoteType } from "@/features/NotesManager/types";
import type { TableRowSelection } from "antd/es/table/interface";
import type { MaybeType } from "@/shared/types";
import { createNotesTableData } from "../helpers";

export const useNotesTable = (
  noteType?: NoteType,
  directionFilter?: keyof typeof DEFAULT_NOTE_DIRECTIONS,
  currentPerson?: MaybeType<Person>,
) => {
  const dispatch = useAppDispatch();
  const { data: notesResponse, isLoading: notesIsLoading } = useGetAllNotesQuery();
  const { data: personsResponse, isLoading: personsIsLoading } = useGetAllPersonsQuery();

  const noteTitles: string[] = [];

  const [deleteNotesByIds] = useDeleteNotesByIdsMutation();

  const { callError } = useAlert();

  const deleteModalIsOpen = useAppSelector(notesTableSliceSelectors.selectDeleteModalIsOpen);
  const selectedIds = useAppSelector(notesTableSliceSelectors.selectSelectedTableIds);
  let dataSource =
    noteType?.length && noteType !== DEFAULT_NOTE_TYPES.DEFAULT_NOTE
      ? useAppSelector(notesManagerSelectors.getAllNotes).filter(
          (item) => item.noteType.name === noteType,
        )
      : useAppSelector(notesManagerSelectors.getAllNotes);

  if (currentPerson) {
    dataSource = dataSource.filter((item) => item.personId === currentPerson.id);
  }

  const onSelectChange = (selectedKeys: Key[]) =>
    dispatch(notesTableSliceActions.setSelectedRowKeys(selectedKeys));

  const handleOpenModal = useCallback(() => {
    dispatch(notesTableSliceActions.openDeleteModal());
  }, [dispatch]);

  const handleCloseModal = useCallback(() => {
    dispatch(notesTableSliceActions.closeDeleteModal());
  }, [dispatch]);

  const deleteSelected = useCallback((ids: string[]) => {
    deleteNotesByIds(ids)
      .unwrap()
      .then(() => {
        handleCloseModal();
        dispatch(notesTableSliceActions.setSelectedRowKeys([]));
      })
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      });
  }, []);

  const rowSelection: TableRowSelection<NotesTableItem> = useMemo(
    () => ({
      selectedIds,
      onChange: onSelectChange,
      columnWidth: 35,
    }),
    [selectedIds],
  );

  const handleClickRow = useCallback(
    (note: NotesTableItem) => {
      dispatch(notesManagerActions.addSelectedNote(note));
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(notesTableSliceActions.setSelectedRowKeys([]));
    };
  }, []);

  useEffect(() => {
    if (isNotVoid(notesResponse) && isNotVoid(personsResponse?.persons)) {
      const notesData = createNotesTableData(notesResponse, personsResponse?.persons ?? []);
      dispatch(notesManagerActions.setNotes(notesData));
    }
  }, [notesResponse, personsResponse]);

  if (directionFilter && directionFilter !== DEFAULT_NOTE_DIRECTIONS.ALL) {
    dataSource = dataSource.filter((item) => item.direction?.includes(directionFilter) ?? false);
  }

  const setSelectedNotes = (notesTableItems: NotesTableItem[]) => {
    dispatch(notesManagerActions.setSelectedNotes(notesTableItems));
  };

  const selectedTableNotes = useMemo(() => {
    return dataSource?.filter((note) => selectedIds.includes(note.id)) ?? [];
  }, [dataSource, selectedIds]);

  return {
    state: {
      dataSource,
      isLoading: notesIsLoading || personsIsLoading,
      selectedIds,
      rowSelection,
      deleteModalIsOpen,
      selectedTableNotes,
      noteTitles,
    },
    methods: {
      handleOpenModal,
      handleCloseModal,
      deleteSelected,
      handleClickRow,
      onSelectChange,
      setSelectedNotes,
    },
  };
};
